import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Grid,
  Divider,
  Alert,
  Chip,
  Paper,
  useTheme,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  IconButton,
  Collapse,
  DialogContentText,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  Person, 
  CalendarToday, 
  LocalHospital,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  ExpandLess as ExpandLessIcon,
  AccessTime as AccessTimeIcon,
  Assignment as AssignmentIcon,
  MedicalServicesOutlined,
  AssignmentOutlined,
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  Visibility as VisibilityIcon,
  FolderOpen as FolderIcon,
  Science as ScienceIcon
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { updateProcedureRequest, fetchPlanOfCareByUserId, fetchHealthDocuments } from '../../services/healinApi';
import { alpha } from '@mui/material/styles';

function ProcedureRequestDetail({ procedureRequest: initialProcedureRequest, open, onClose, onIssueProcedureRequest, onIssued, onClaimed }) {
  const theme = useTheme();
  const [procedureRequest, setProcedureRequest] = useState(initialProcedureRequest);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [issuingProcedureRequest, setIssuingProcedureRequest] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [expandedProcedure, setExpandedProcedure] = useState(null);
  const [editingProcedureId, setEditingProcedureId] = useState(null);
  const [editedInstructions, setEditedInstructions] = useState('');
  const [planOfCare, setPlanOfCare] = useState(null);
  const [planOfCareLoading, setPlanOfCareLoading] = useState(false);
  const [healthDocuments, setHealthDocuments] = useState(null);
  const [healthDocumentsLoading, setHealthDocumentsLoading] = useState(false);
  const [healthDocumentsError, setHealthDocumentsError] = useState(null);
  const [showHealthDocuments, setShowHealthDocuments] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const fetchPlanOfCare = useCallback(async () => {
    if (!procedureRequest?.userId || procedureRequest?.status !== 'CLAIMED') return;

    setPlanOfCareLoading(true);
    
    try {
      console.log('Fetching plan of care for user:', procedureRequest.userId);
      const planData = await fetchPlanOfCareByUserId(procedureRequest.userId);
      setPlanOfCare(planData);
      console.log('Plan of care loaded:', planData);
    } catch (error) {
      console.error('Error fetching plan of care:', error);
      // Silently handle errors - plan of care is supplementary information
    } finally {
      setPlanOfCareLoading(false);
    }
  }, [procedureRequest?.userId, procedureRequest?.status]);

  // Fetch Plan of Care only for claimed procedure requests
  useEffect(() => {
    if (procedureRequest && procedureRequest.userId && procedureRequest.status === 'CLAIMED') {
      fetchPlanOfCare();
    }
  }, [procedureRequest, fetchPlanOfCare]);

  const handleCheckHealthDocuments = async () => {
    if (!procedureRequest?.userId || !planOfCare?.profileID) {
      enqueueSnackbar('Unable to fetch health documents: missing user or profile information', { variant: 'error' });
      return;
    }

    setHealthDocumentsLoading(true);
    setHealthDocumentsError(null);
    
    try {
      console.log('Fetching health documents for:', {
        userId: procedureRequest.userId,
        identityId: planOfCare.profileID
      });
      
      const documentsData = await fetchHealthDocuments(procedureRequest.userId, planOfCare.profileID);
      setHealthDocuments(documentsData);
      setShowHealthDocuments(true);
      enqueueSnackbar('Health documents loaded successfully', { variant: 'success' });
      console.log('Health documents loaded:', documentsData);
    } catch (error) {
      console.error('Error fetching health documents:', error);
      setHealthDocumentsError(error.message);
      enqueueSnackbar(`Error loading health documents: ${error.message}`, { variant: 'error' });
    } finally {
      setHealthDocumentsLoading(false);
    }
  };

  const handleToggleExpand = (procedureId) => {
    setExpandedProcedure(expandedProcedure === procedureId ? null : procedureId);
  };

  const handleEditProcedure = (procedureId) => {
    const procedure = findProcedureById(procedureId);
    if (procedure) {
      setEditingProcedureId(procedureId);
      setEditedInstructions(getProcedureInstructions(procedure));
    }
  };

  const handleCancelEdit = () => {
    setEditingProcedureId(null);
    setEditedInstructions('');
  };

  const handleSaveProcedure = (procedureId) => {
    const updatedProcedures = procedureRequest.procedures.map(proc => {
      const currentProcId = typeof proc === 'string' ? proc : (proc.id || proc.procedureId);
      if (currentProcId === procedureId) {
        // If proc is a string (just an ID), create a new object
        if (typeof proc === 'string') {
          return {
            procedureId: proc,
            instructions: editedInstructions
          };
        } 
        // Otherwise update the existing object
        return {
          ...proc,
          instructions: editedInstructions
        };
      }
      return proc;
    });

    setProcedureRequest({
      ...procedureRequest,
      procedures: updatedProcedures
    });
    
    setEditingProcedureId(null);
    // Here you would typically call an API to update the procedure request
  };

  // Helper to find a procedure by ID
  const findProcedureById = (procedureId) => {
    return procedureRequest.procedures.find(proc => {
      const currentProcId = typeof proc === 'string' ? proc : (proc.id || proc.procedureId);
      return currentProcId === procedureId;
    });
  };

  const handleOpenConfirmDialog = () => {
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  const handleIssueProcedureRequest = async () => {
    try {
      setIssuingProcedureRequest(true);
      
      console.log('Issuing procedure request for:', {
        userId: procedureRequest.userId,
        requestId: procedureRequest.requestId,
        notes: notes
      });
      
      // Call the API to update procedure request status from CLAIMED to ISSUED
      const response = await updateProcedureRequest({
        userId: procedureRequest.userId,
        requestId: procedureRequest.requestId,
        status: 'ISSUED',
        doctorComments: notes || 'Procedure request issued successfully',
        issuedAt: new Date().toISOString()
      });
      
      console.log('Procedure request issued successfully:', response);
      
      // Update the procedure request object with new status
      const updatedProcedureRequest = {
        ...procedureRequest,
        status: 'ISSUED',
        issueDate: new Date().toISOString(),
        notes: notes
      };
      
      // Call the parent callback if provided
      if (onIssued) {
        onIssued(updatedProcedureRequest);
      }
      
      // Close the dialog and show success
      setConfirmDialogOpen(false);
      enqueueSnackbar('Procedure request issued successfully', { variant: 'success' });
      
      // Close the detail view after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);
      
    } catch (error) {
      console.error('Error issuing procedure request:', error);
      enqueueSnackbar(`Error issuing procedure request: ${error.message}`, { variant: 'error' });
    } finally {
      setIssuingProcedureRequest(false);
    }
  };

  const handleClaimProcedureRequest = async () => {
    try {
      setIsSubmitting(true);
      
      // Get the procedures in the correct format for the API
      const formattedProcedures = procedureRequest.procedures.map(proc => {
        // If proc is already a string (just an ID), return it
        if (typeof proc === 'string') return proc;
        // Otherwise return the ID
        return proc.procedureId || proc.id;
      });

      // Prepare claim payload
      const claimPayload = {
        requestId: procedureRequest.requestId || procedureRequest.id,
        procedures: formattedProcedures,
        notes: notes || '' // Use the notes state as notes for claiming
      };

      console.log('Claiming procedure request with payload:', claimPayload);
      
      // In a real app, you would call your API here
      // const response = await claimProcedureRequest(claimPayload);
      
      // For demo, simulate success
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      onClaimed({
        ...procedureRequest,
        status: 'CLAIMED',
        claimDate: new Date().toISOString(),
        notes: notes
      });
      
      enqueueSnackbar('Procedure request claimed successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error claiming procedure request:', error);
      enqueueSnackbar(`Error claiming procedure request: ${error.message}`, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Helper functions to get procedure details
  const getProcedureName = (procedure) => {
    if (typeof procedure === 'string') return 'Unknown Procedure';
    // Try all possible property names for procedure name
    return procedure.procedureName || procedure.name || 
           (procedure.procedure ? procedure.procedure.name : 'Unnamed Procedure');
  };

  const getProcedureType = (procedure) => {
    if (typeof procedure === 'string') return 'Not specified';
    return procedure.type || procedure.procedureClass || 
           (procedure.procedure ? procedure.procedure.type : 'Not specified');
  };

  const getProcedureInstructions = (procedure) => {
    if (typeof procedure === 'string') return 'No specific instructions';
    return procedure.instructions || procedure.description || 
           (procedure.procedure ? (procedure.procedure.instructions || procedure.procedure.description) : 'No specific instructions');
  };

  // Get procedure type color based on class
  const getProcedureTypeColor = (procedureType) => {
    if (!procedureType) return { color: 'default', bgcolor: alpha(theme.palette.grey[500], 0.1) };
    
    const type = procedureType.toLowerCase();
    
    // Define color mapping for different procedure classes
    if (type.includes('laboratório') || type.includes('laboratorio') || type.includes('lab')) {
      return { 
        color: theme.palette.success.main, 
        bgcolor: alpha(theme.palette.success.main, 0.1),
        borderColor: alpha(theme.palette.success.main, 0.3)
      };
    } else if (type.includes('exames complementares') || type.includes('exame') || type.includes('imaging') || type.includes('radiology')) {
      return { 
        color: theme.palette.info.main, 
        bgcolor: alpha(theme.palette.info.main, 0.1),
        borderColor: alpha(theme.palette.info.main, 0.3)
      };
    } else if (type.includes('cirurgia') || type.includes('surgery') || type.includes('procedimento') || type.includes('procedure')) {
      return { 
        color: theme.palette.error.main, 
        bgcolor: alpha(theme.palette.error.main, 0.1),
        borderColor: alpha(theme.palette.error.main, 0.3)
      };
    } else if (type.includes('consulta') || type.includes('consultation') || type.includes('appointment')) {
      return { 
        color: theme.palette.warning.main, 
        bgcolor: alpha(theme.palette.warning.main, 0.1),
        borderColor: alpha(theme.palette.warning.main, 0.3)
      };
    } else if (type.includes('terapia') || type.includes('therapy') || type.includes('rehabilitation')) {
      return { 
        color: theme.palette.secondary.main, 
        bgcolor: alpha(theme.palette.secondary.main, 0.1),
        borderColor: alpha(theme.palette.secondary.main, 0.3)
      };
    } else {
      // Default color for unknown types
      return { 
        color: theme.palette.primary.main, 
        bgcolor: alpha(theme.palette.primary.main, 0.1),
        borderColor: alpha(theme.palette.primary.main, 0.3)
      };
    }
  };

  // Render a procedure item with all its details
  const renderProcedureItem = (procedure, index) => {
    // Debug: Log the procedure structure to help identify issues
    console.log(`Procedure ${index}:`, procedure);
    
    const procedureName = getProcedureName(procedure);
    const procedureType = getProcedureType(procedure);
    const procedureInstructions = getProcedureInstructions(procedure);
    
    const procedureId = typeof procedure === 'string' ? procedure : (procedure.id || procedure.procedureId);

    return (
      <Card 
        key={procedureId || index} 
        variant="outlined" 
        sx={{ 
          mb: 2, 
          borderLeft: `4px solid ${theme.palette.secondary.main}`,
          bgcolor: editingProcedureId === procedureId ? 'rgba(0, 0, 0, 0.02)' : 'transparent'
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Typography variant="h6" component="h4">
              {procedureName}
            </Typography>
            <Box>
              {editingProcedureId === procedureId ? (
                <>
                  <IconButton size="small" onClick={() => handleSaveProcedure(procedureId)} color="primary">
                    <SaveIcon />
                  </IconButton>
                  <IconButton size="small" onClick={handleCancelEdit} color="error">
                    <CancelIcon />
                  </IconButton>
                </>
              ) : (
                <IconButton size="small" onClick={() => handleEditProcedure(procedureId)}>
                  <EditIcon />
                </IconButton>
              )}
              <IconButton size="small" onClick={() => handleToggleExpand(procedureId)}>
                {expandedProcedure === procedureId ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">Type:</Typography>
              <Chip 
                label={procedureType} 
                size="small" 
                sx={{ 
                  height: 24, 
                  fontSize: '0.75rem',
                  bgcolor: getProcedureTypeColor(procedureType).bgcolor,
                  color: getProcedureTypeColor(procedureType).color,
                  border: `1px solid ${getProcedureTypeColor(procedureType).borderColor}`,
                  mt: 0.5
                }} 
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">Completion Status:</Typography>
              <Typography variant="body2">
                {typeof procedure === 'string' ? 'Not specified' : 
                 `${procedure.completedCount || 0} of ${procedure.numberProcedures || 1} completed`}
              </Typography>
            </Grid>
          </Grid>

          <Collapse in={expandedProcedure === procedureId} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Instructions:</Typography>
              {editingProcedureId === procedureId ? (
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  value={editedInstructions}
                  onChange={(e) => setEditedInstructions(e.target.value)}
                  margin="dense"
                />
              ) : (
                <Typography variant="body2">{procedureInstructions}</Typography>
              )}

              {typeof procedure !== 'string' && procedure.completionDates && procedure.completionDates.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Completion History:</Typography>
                  {procedure.completionDates.map((date, idx) => (
                    <Typography key={idx} variant="body2" color="success.main">
                      • Completed on {formatDate(date)}
                    </Typography>
                  ))}
                </Box>
              )}

              {typeof procedure !== 'string' && procedure.warnings && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Warnings:</Typography>
                  <Typography variant="body2">{procedure.warnings}</Typography>
                </Alert>
              )}

              {typeof procedure !== 'string' && procedure.contraindications && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Contraindications:</Typography>
                  <Typography variant="body2">{procedure.contraindications}</Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  };

  if (!procedureRequest) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">No procedure request selected</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: theme.palette.secondary.main, mr: 2 }}>
              <Person />
            </Avatar>
            <Box>
              <Typography variant="h5" component="h2">
                {procedureRequest.patientName || 'Unknown Patient'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Patient ID: {procedureRequest.patientId || procedureRequest.userId || 'N/A'}
              </Typography>
            </Box>
          </Box>
          <Chip 
            label={procedureRequest.status || 'Unknown'} 
            color={procedureRequest.status === 'CLAIMED' ? 'secondary' : 'default'}
            sx={{ fontWeight: 'bold' }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                avatar={<InfoIcon color="primary" />}
                title="Patient Information"
                titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                sx={{ pb: 1 }}
              />
              <CardContent sx={{ pt: 0 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <InfoIcon fontSize="small" sx={{ mr: 1, color: theme.palette.info.main, fontSize: '0.9rem' }} />
                      Age: {procedureRequest.patientInfo?.age || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <InfoIcon fontSize="small" sx={{ mr: 1, color: theme.palette.info.main, fontSize: '0.9rem' }} />
                      Gender: {procedureRequest.patientInfo?.gender || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocalHospital fontSize="small" sx={{ mr: 1, color: theme.palette.info.main, fontSize: '0.9rem' }} />
                      Blood Type: {procedureRequest.patientInfo?.bloodType || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <InfoIcon fontSize="small" sx={{ mr: 1, color: theme.palette.info.main, fontSize: '0.9rem' }} />
                      Nationality: {procedureRequest.patientInfo?.nationality || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Contact Information:
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Email:</strong> {procedureRequest.patientInfo?.email || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Phone:</strong> {procedureRequest.patientInfo?.phone || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Address:</strong> {procedureRequest.patientInfo?.address || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>City:</strong> {procedureRequest.patientInfo?.city || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Postal Code:</strong> {procedureRequest.patientInfo?.postalCode || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Country:</strong> {procedureRequest.patientInfo?.country || 'N/A'}
                    </Typography>
                  </Grid>
                  {procedureRequest.patientInfo?.fiscalNumber && procedureRequest.patientInfo.fiscalNumber !== 'N/A' && (
                    <Grid item xs={12}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Fiscal Number:</strong> {procedureRequest.patientInfo.fiscalNumber}
                      </Typography>
                    </Grid>
                  )}
                </Grid>

                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                  Chronic Conditions:
                </Typography>
                {/* Show conditions from Plan of Care for claimed procedure requests, otherwise fallback to patientInfo */}
                {(() => {
                  // Try to get conditions from Plan of Care first (only for claimed procedure requests)
                  let conditions = [];
                  
                  if (procedureRequest.status === 'CLAIMED' && planOfCare && planOfCare.Pathways) {
                    try {
                      const pathways = JSON.parse(planOfCare.Pathways);
                      conditions = pathways.map(pathway => ({
                        name: pathway.patologia_de_base,
                        icd10: pathway.ICD10,
                        id: pathway.ID_Pathway
                      }));
                    } catch (error) {
                      console.error('Error parsing pathways:', error);
                    }
                  }
                  
                  // Fallback to patientInfo conditions if no pathways found or not a claimed procedure request
                  if (conditions.length === 0 && procedureRequest.patientInfo?.chronicConditions) {
                    conditions = procedureRequest.patientInfo.chronicConditions.map(condition => ({
                      name: condition
                    }));
                  }
                  
                  return conditions.length > 0 ? (
                    <Box sx={{ mt: 0, mb: 2 }}>
                      <Grid container spacing={1}>
                        {conditions.map((condition, index) => (
                          <Grid item key={index}>
                            <Chip 
                              label={condition.icd10 ? `${condition.name} (${condition.icd10})` : condition.name}
                              color="secondary" 
                              variant="outlined"
                              size="small"
                              sx={{ mb: 1 }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                      {conditions.some(c => c.icd10) && procedureRequest.status === 'CLAIMED' && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Conditions shown with ICD-10 codes from Plan of Care
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {planOfCareLoading ? 'Loading conditions...' : 'No chronic conditions recorded'}
                    </Typography>
                  );
                })()}

                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                  Allergies:
                </Typography>
                {procedureRequest.patientInfo?.allergies && procedureRequest.patientInfo.allergies.length > 0 ? (
                  <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2 }}>
                    {procedureRequest.patientInfo.allergies.map((allergy, index) => (
                      <Typography component="li" variant="body2" key={index} color="error">
                        {allergy}
                      </Typography>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">No allergies recorded</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardHeader
                  avatar={<CalendarToday color="primary" />}
                  title="Procedure Request Details"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                  sx={{ pb: 1 }}
                />
              <CardContent sx={{ pt: 0 }}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: theme.palette.info.main, fontSize: '0.9rem' }} />
                  Requested: {formatDate(procedureRequest.requestedAt || procedureRequest.requestDate)}
                </Typography>
                
                {procedureRequest.assignedAt && (
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AssignmentIcon fontSize="small" sx={{ mr: 1, color: theme.palette.info.main, fontSize: '0.9rem' }} />
                    Claimed: {formatDate(procedureRequest.assignedAt)}
                  </Typography>
                )}
                
                {procedureRequest.assignedDoctorId && (
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Person fontSize="small" sx={{ mr: 1, color: theme.palette.info.main, fontSize: '0.9rem' }} />
                    Claimed by: {procedureRequest.assignedDoctorId}
                  </Typography>
                )}
                
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                  Request Notes:
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                  {procedureRequest.userComments || procedureRequest.notes || 'No notes provided'}
                </Typography>
              </CardContent>
            </Card>

            {/* Plan of Care Section - Summary (only for claimed procedure requests) */}
            {planOfCare && procedureRequest.status === 'CLAIMED' && (
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardHeader
                  avatar={<MedicalServicesOutlined color="primary" />}
                  title="Plan of Care Summary"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ pt: 0 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Plan Status:
                  </Typography>
                  <Chip 
                    label="Active Plan Available" 
                    color="success" 
                    size="small"
                    sx={{ mb: 2 }}
                  />
                  
                  {planOfCare.LastUpdated && (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Last Updated:
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {formatDate(planOfCare.LastUpdated)}
                      </Typography>
                    </>
                  )}

                  {planOfCare.Pathways && (() => {
                    try {
                      const pathways = JSON.parse(planOfCare.Pathways);
                      return (
                        <>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Active Pathways:
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {pathways.length} clinical pathway{pathways.length !== 1 ? 's' : ''} identified
                          </Typography>
                        </>
                      );
                    } catch {
                      return null;
                    }
                  })()}

                  {/* Health Documents Button */}
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={healthDocumentsLoading ? <CircularProgress size={16} /> : <FolderIcon />}
                      onClick={handleCheckHealthDocuments}
                      disabled={healthDocumentsLoading || !planOfCare.profileID}
                      size="small"
                      fullWidth
                      sx={{ 
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      {healthDocumentsLoading ? 'Loading...' : 'Check Health Documents'}
                    </Button>
                  </Box>

                  {planOfCare.MedicalTerms && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Clinical Summary:
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        fontStyle: 'italic',
                        maxHeight: '80px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        {planOfCare.MedicalTerms.substring(0, 150)}...
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Health Documents Section - positioned under Plan of Care */}
            {(showHealthDocuments || healthDocuments) && (
              <Card sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: 'info.main' }}>
                      <FolderIcon />
                    </Avatar>
                  }
                  title="Health Documents"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                  subheader={`Documents for ${procedureRequest?.userDetail?.FirstName || 'Patient'} ${procedureRequest?.userDetail?.LastName || ''}`}
                  action={
                    <IconButton onClick={() => setShowHealthDocuments(!showHealthDocuments)}>
                      {showHealthDocuments ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  }
                />
                <Collapse in={showHealthDocuments}>
                  <CardContent>
                  {healthDocumentsError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {healthDocumentsError}
                    </Alert>
                  )}
                  
                  {healthDocuments && healthDocuments.files ? (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Found {healthDocuments.files.length} document{healthDocuments.files.length !== 1 ? 's' : ''} 
                        {healthDocuments.files.length > 5 && ' (scroll to see more)'}
                      </Typography>
                      
                      {/* Scrollable container for documents */}
                      <Box
                        sx={{
                          maxHeight: '400px', // Height for approximately 5 documents
                          overflowY: 'auto',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          '&::-webkit-scrollbar': {
                            width: '8px',
                          },
                          '&::-webkit-scrollbar-track': {
                            backgroundColor: 'grey.100',
                            borderRadius: '4px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'grey.400',
                            borderRadius: '4px',
                            '&:hover': {
                              backgroundColor: 'grey.600',
                            },
                          },
                        }}
                      >
                        <List sx={{ py: 0 }}>
                          {healthDocuments.files.map((file, index) => {
                            const getFileIcon = (fileType) => {
                              const type = fileType?.toLowerCase();
                              if (type === 'pdf') return <PdfIcon color="error" />;
                              if (['jpg', 'jpeg', 'png', 'gif'].includes(type)) return <ImageIcon color="primary" />;
                              if (['doc', 'docx', 'txt'].includes(type)) return <DescriptionIcon color="info" />;
                              return <FileIcon />;
                            };

                            return (
                              <ListItem 
                                key={index} 
                                divider={index < healthDocuments.files.length - 1}
                                sx={{ 
                                  py: 1.5,
                                  '&:hover': {
                                    backgroundColor: 'action.hover',
                                  }
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: '40px' }}>
                                  {getFileIcon(file.FileType)}
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {file.FileName}
                                    </Typography>
                                  }
                                  secondary={
                                    <Box>
                                      <Typography variant="caption" display="block">
                                        Size: {file.SizeKB} KB • Type: {file.FileType?.toUpperCase() || 'Unknown'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Last Modified: {file.LastModified}
                                      </Typography>
                                    </Box>
                                  }
                                />
                                <Button
                                  component="a"
                                  href={file.PreviewURL}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  variant="outlined"
                                  size="small"
                                  startIcon={<VisibilityIcon />}
                                  sx={{ 
                                    minWidth: 'auto',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    ml: 1,
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    borderColor: 'primary.main',
                                    color: 'primary.main',
                                    '&:hover': {
                                      backgroundColor: 'primary.main',
                                      color: 'white',
                                      borderColor: 'primary.main',
                                    }
                                  }}
                                >
                                  View
                                </Button>
                              </ListItem>
                            );
                          })}
                        </List>
                      </Box>
                      
                      {/* Document count summary at bottom */}
                      {healthDocuments.files.length > 5 && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                          Showing all {healthDocuments.files.length} documents • Scroll up to see more
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No documents available
                    </Typography>
                  )}
                  </CardContent>
                </Collapse>
              </Card>
            )}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Box>
          <Typography variant="h6" gutterBottom>
            Procedures ({procedureRequest.procedures?.length || 0})
        </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {procedureRequest.procedures && procedureRequest.procedures.length > 0 ? (
            <Grid container spacing={2}>
              {procedureRequest.procedures.map((procedure, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  {renderProcedureItem(procedure, index)}
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ my: 2, textAlign: 'center' }}>
              No procedures found for this request.
            </Typography>
          )}
                </Box>

        {procedureRequest.status === 'CLAIMED' && (
          <>
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                Issue Procedure Request
              </Typography>
              <TextField
                fullWidth
                label="Doctor's Notes"
                multiline
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                variant="outlined"
                placeholder="Add any notes or instructions for the patient..."
                sx={{ mb: 2 }}
              />
              
              {/* Embedded Procedure Request Software */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" component="h3" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScienceIcon color="secondary" />
                  Certified Procedure Request Software
                </Typography>
                <Alert 
                  severity="info" 
                  sx={{ 
                    mb: 2, 
                    borderRadius: 1,
                    '& .MuiAlert-message': {
                      fontSize: '0.875rem'
                    }
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Patient: {procedureRequest.patientName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Use the certified procedure request software below to create the official procedure request.
                  </Typography>
                </Alert>
                <Box sx={{ 
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  overflow: 'hidden',
                  height: '600px',
                  bgcolor: 'background.paper'
                }}>
                  <iframe
                    src="https://www.ccare.eu/icare/log_ent.home"
                    width="100%"
                    height="100%"
                    style={{ 
                      border: 'none',
                      display: 'block'
                    }}
                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                    title="Certified Procedure Request Software"
                    loading="lazy"
                  />
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button variant="outlined" onClick={onClose}>
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  color="secondary" 
                  onClick={handleOpenConfirmDialog}
                  startIcon={<CheckCircleIcon />}
                >
                  Issue Procedure Request
                </Button>
              </Box>
            </Box>
          </>
        )}

        {procedureRequest.status !== 'CLAIMED' && procedureRequest.status !== 'ISSUED' && (
          <Button
            variant="contained"
            color="secondary"
            onClick={handleClaimProcedureRequest}
            startIcon={<AssignmentOutlined />}
            disabled={isSubmitting}
            sx={{ mt: 3 }}
          >
            Claim Procedure Request
            {isSubmitting && <CircularProgress size={24} sx={{ ml: 1 }} />}
          </Button>
        )}
      </Paper>

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Confirm Procedure Request Issuance
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to issue this procedure request for {procedureRequest.patientName || 'the patient'}? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleIssueProcedureRequest} 
            color="secondary" 
            variant="contained"
            disabled={issuingProcedureRequest}
            autoFocus
          >
            {issuingProcedureRequest ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
            </Dialog>
    </Box>
  );
}

ProcedureRequestDetail.propTypes = {
  procedureRequest: PropTypes.shape({
    requestId: PropTypes.string.isRequired,
    patientName: PropTypes.string.isRequired,
    patientId: PropTypes.string,
    userId: PropTypes.string.isRequired,
    patientInfo: PropTypes.shape({
      age: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      gender: PropTypes.string,
      bloodType: PropTypes.string,
      allergies: PropTypes.array,
      chronicConditions: PropTypes.array,
      email: PropTypes.string,
      phone: PropTypes.string,
      address: PropTypes.string,
      city: PropTypes.string,
      country: PropTypes.string,
      postalCode: PropTypes.string,
      fiscalNumber: PropTypes.string
    }),
    procedures: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          id: PropTypes.string,
          procedureId: PropTypes.string,
          name: PropTypes.string,
          procedureName: PropTypes.string,
          type: PropTypes.string,
          description: PropTypes.string,
          instructions: PropTypes.string,
          completedCount: PropTypes.number,
          numberProcedures: PropTypes.number,
          completionDates: PropTypes.array
        })
      ])
    ).isRequired,
    requestedAt: PropTypes.string,
    requestDate: PropTypes.string,
    status: PropTypes.string.isRequired,
    notes: PropTypes.string,
    userComments: PropTypes.string,
    assignedDoctorId: PropTypes.string,
    assignedAt: PropTypes.string
  }).isRequired,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onIssueProcedureRequest: PropTypes.func,
  onIssued: PropTypes.func,
  onClaimed: PropTypes.func
};

export default ProcedureRequestDetail; 